import React, { useState, useEffect, useCallback } from 'react';
import { prsService, evidenceService, PrsReadinessReport } from '../../../services/api';
import { getAppMarket } from '../../../services/MarketService';
import { safeText } from '../../../utils/formatAddress';

interface PRSWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

type WizardStep = 'READINESS' | 'FIX_FIELDS' | 'EXPORT' | 'CHECKLIST' | 'SUBMIT';

async function calculateSHA256(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const buffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function PRSWizard({ isOpen, onClose, onSuccess }: PRSWizardProps) {
    const [step, setStep] = useState<WizardStep>('READINESS');
    const [report, setReport] = useState<PrsReadinessReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportDownloaded, setExportDownloaded] = useState(false);
    const [logging, setLogging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [completed, setCompleted] = useState(false);

    // F-307: PRS is UK terminology — the Texas market brands the identical
    // flow as Texas Registration (wizard steps and evidence logging unchanged).
    const isTexas = getAppMarket() === 'texas';
    const WIZARD_NAME = isTexas ? 'Texas Registration Wizard' : 'PRS Submission Wizard';
    const SUBTITLE = isTexas
        ? 'Step-by-step guide to prepare your municipal rental registration'
        : 'Step-by-step guide to prepare for MHCLG PRS Database registration';
    const PRS_TERMS = isTexas ? 'rental registration' : 'PRS Database';

    useEffect(() => {
        if (isOpen) {
            setStep('READINESS');
            setExportDownloaded(false);
            setError(null);
            setCompleted(false);
            loadReadiness();
        }
    }, [isOpen]);

    const loadReadiness = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await prsService.getReadiness();
            setReport(res.data?.data || null);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || (getAppMarket() === 'texas' ? 'Failed to load registration readiness' : 'Failed to load PRS readiness'));
        } finally {
            setLoading(false);
        }
    }, []);

    const handleNext = () => {
        setError(null);
        if (step === 'READINESS') { setStep('FIX_FIELDS'); return; }
        if (step === 'FIX_FIELDS') { setStep('EXPORT'); return; }
        if (step === 'EXPORT') { setStep('CHECKLIST'); return; }
        if (step === 'CHECKLIST') { setStep('SUBMIT'); return; }
    };

    const handleBack = () => {
        if (step === 'FIX_FIELDS') setStep('READINESS');
        else if (step === 'EXPORT') setStep('FIX_FIELDS');
        else if (step === 'CHECKLIST') setStep('EXPORT');
        else if (step === 'SUBMIT') setStep('CHECKLIST');
    };

    const handleExport = async (propertyId?: string) => {
        setExporting(true);
        try {
            const res = await prsService.exportCsv(propertyId);
            const blob = new Blob([res.data as any], { type: 'text/csv;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const suffix = propertyId ? `-property-${propertyId}` : '-portfolio';
            a.download = `prs-export${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setExportDownloaded(true);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const logCompletion = async () => {
        setLogging(true);
        setError(null);
        try {
            const readinessScore = report?.summary?.readinessPercent ?? 0;
            const eventData = {
                event: 'PRS_WIZARD_COMPLETED',
                timestamp: new Date().toISOString(),
                portfolioReadinessScore: readinessScore,
                exportDownloaded,
                totalProperties: report?.summary?.total ?? 0,
                greenCount: report?.summary?.green ?? 0,
                amberCount: report?.summary?.amber ?? 0,
                redCount: report?.summary?.red ?? 0,
            };
            const jsonContent = JSON.stringify(eventData, null, 2);
            const contentHash = await calculateSHA256(jsonContent);
            const fileName = `prs-wizard-completion-${new Date().toISOString().slice(0, 10)}.json`;
            const blob = new Blob([jsonContent], { type: 'application/json' });
            const file = new File([blob], fileName, { type: 'application/json' });

            const firstProperty = report?.items?.[0];
            const propertyId = firstProperty?.propertyId || 'portfolio';

            const urlResponse = await evidenceService.getPresignedUrl({
                fileName,
                fileType: 'application/json',
                propertyId,
            });
            const { presignedUrl, s3Key } = urlResponse.data.data;

            await fetch(presignedUrl, {
                method: 'PUT',
                body: blob,
                headers: { 'Content-Type': 'application/json' }
            });

            await evidenceService.confirmUpload({
                propertyId,
                fileName,
                s3Key,
                fileSize: blob.size,
                fileType: 'application/json',
                contentHash,
                type: 'PRS_WIZARD',
                description: `${WIZARD_NAME} completed — readiness score ${readinessScore}%`
            });

            setCompleted(true);
            setTimeout(() => { onClose(); if (onSuccess) onSuccess(); }, 2000);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to log completion');
        } finally {
            setLogging(false);
        }
    };

    if (!isOpen) return null;

    const renderStepIndicator = () => {
        const labels = ['Readiness', 'Fix Fields', 'Export CSV', 'Checklist', 'Submit'];
        const stepNames: WizardStep[] = ['READINESS', 'FIX_FIELDS', 'EXPORT', 'CHECKLIST', 'SUBMIT'];
        const currentIdx = stepNames.indexOf(step);

        return (
            <div className="flex items-center justify-center mb-8 overflow-x-auto pb-2">
                {labels.map((label, idx) => {
                    const isCompleted = idx < currentIdx;
                    const isCurrent = idx === currentIdx;
                    return (
                        <React.Fragment key={label}>
                            <div className="flex flex-col items-center min-w-[2.5rem]">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1 transition-all duration-300
                                    ${isCompleted ? 'bg-indigo-600 text-white shadow-sm' :
                                        isCurrent ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500 scale-110' : 'bg-gray-100 text-gray-400'}`}>
                                    {isCompleted ? <i className="fas fa-check"></i> : idx + 1}
                                </div>
                                <span className={`text-[9px] uppercase tracking-wider text-center whitespace-nowrap ${isCurrent ? 'font-bold text-indigo-700' : 'text-gray-400 font-medium'}`}>{label}</span>
                            </div>
                            {idx < labels.length - 1 && (
                                <div className={`h-[2px] w-6 sm:w-8 mx-0.5 mb-5 transition-colors duration-300 ${isCompleted ? 'bg-indigo-600' : 'bg-gray-100'}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>

                <div className="relative inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-2xl">
                    <div className="flex justify-between items-center mb-4 border-b pb-3">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                <i className={`fas ${isTexas ? 'fa-clipboard-check text-emerald-600' : 'fa-database text-indigo-600'} mr-2`}></i>
                                {WIZARD_NAME}
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {SUBTITLE}
                            </p>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><i className="fas fa-times"></i></button>
                    </div>

                    {renderStepIndicator()}

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-wrap">
                            <i className="fas fa-exclamation-circle mr-2"></i>{error}
                        </div>
                    )}

                    <div className="min-h-[350px]">
                        {/* Step 1: Readiness Check */}
                        {step === 'READINESS' && (
                            <div className="space-y-5 animate-in fade-in duration-500">
                                <div className="p-5 bg-indigo-50 rounded-2xl border border-indigo-100">
                                    <div className="flex items-center">
                                        <i className="fas fa-chart-pie text-2xl text-indigo-600 mr-4"></i>
                                        <div>
                                            <h3 className="font-bold text-indigo-900 text-lg">Portfolio Readiness Score</h3>
                                            <p className="text-xs text-indigo-700">
                                                {isTexas
                                                    ? 'How prepared your portfolio is for Texas rental registration'
                                                    : 'How prepared your portfolio is for PRS Database registration'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="text-center py-12 text-gray-500">
                                        <i className="fas fa-spinner fa-spin text-2xl mb-3"></i>
                                        <p>Loading readiness data...</p>
                                    </div>
                                ) : report ? (
                                    <div className="space-y-4">
                                        <div className="bg-white border border-gray-200 rounded-xl p-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Readiness</span>
                                                <span className={`text-3xl font-black ${
                                                    report.summary.readinessPercent >= 80 ? 'text-emerald-600' :
                                                    report.summary.readinessPercent >= 50 ? 'text-amber-600' : 'text-rose-600'
                                                }`}>{report.summary.readinessPercent}%</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-3 mb-4 overflow-hidden">
                                                <div className={`h-full rounded-full transition-all duration-700 ${
                                                    report.summary.readinessPercent >= 80 ? 'bg-emerald-500' :
                                                    report.summary.readinessPercent >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                                }`} style={{ width: `${report.summary.readinessPercent}%` }}></div>
                                            </div>
                                            <div className="grid grid-cols-4 gap-3 text-center">
                                                <div className="bg-emerald-50 rounded-lg p-3">
                                                    <div className="text-xl font-black text-emerald-700">{report.summary.green}</div>
                                                    <div className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Ready</div>
                                                </div>
                                                <div className="bg-amber-50 rounded-lg p-3">
                                                    <div className="text-xl font-black text-amber-700">{report.summary.amber}</div>
                                                    <div className="text-[10px] text-amber-600 font-bold uppercase tracking-wider">Partial</div>
                                                </div>
                                                <div className="bg-rose-50 rounded-lg p-3">
                                                    <div className="text-xl font-black text-rose-700">{report.summary.red}</div>
                                                    <div className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">Action</div>
                                                </div>
                                                <div className="bg-slate-50 rounded-lg p-3">
                                                    <div className="text-xl font-black text-slate-700">{report.summary.total}</div>
                                                    <div className="text-[10px] text-slate-600 font-bold uppercase tracking-wider">Total</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm text-indigo-800">
                                            <i className="fas fa-info-circle mr-2"></i>
                                            <span className="font-medium">Next step:</span> Review properties with missing fields and fix them before exporting.
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-500">
                                        <p>No readiness data available.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 2: Fix Missing Fields */}
                        {step === 'FIX_FIELDS' && (
                            <div className="space-y-5 animate-in slide-in-from-right-4 duration-500">
                                <div className="p-5 bg-amber-50 rounded-2xl border border-amber-100">
                                    <div className="flex items-center">
                                        <i className="fas fa-tasks text-2xl text-amber-600 mr-4"></i>
                                        <div>
                                            <h3 className="font-bold text-amber-900 text-lg">Fix Missing Fields</h3>
                                            <p className="text-xs text-amber-700">
                                                {isTexas
                                                    ? 'Properties below need rental registration fields completed before submission'
                                                    : 'Properties below need PRS fields completed before submission'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {!report || report.items.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500">
                                        <p>No properties found.</p>
                                        <button onClick={loadReadiness} className="mt-3 text-xs font-bold text-indigo-600 hover:text-indigo-800 uppercase tracking-wider">Refresh</button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {report.items.filter(i => i.status !== 'GREEN').length === 0 && (
                                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
                                                <i className="fas fa-check-circle text-3xl text-emerald-500 mb-2"></i>
                                                <p className="font-bold text-emerald-800">All properties are ready!</p>
                                                <p className="text-sm text-emerald-600 mt-1">No missing fields found across your portfolio.</p>
                                            </div>
                                        )}
                                        {report.items.filter(i => i.status !== 'GREEN').map(item => (
                                            <div key={item.propertyId} className={`border rounded-xl p-4 ${
                                                item.status === 'RED' ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'
                                            }`}>
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${item.status === 'RED' ? 'bg-rose-500' : 'bg-amber-500'}`}></span>
                                                        <span className="font-semibold text-gray-900 text-sm">{safeText(item.addressLine1, `Property ${item.propertyId}`)}</span>
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                                                            item.status === 'RED' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                                                        }`}>{safeText(item.status)}</span>
                                                    </div>
                                                </div>
                                                {item.missingRequired.length > 0 && (
                                                    <div className="mt-2 ml-5">
                                                        <p className="text-xs font-bold text-rose-700 mb-1">Missing required fields:</p>
                                                        <ul className="list-disc list-inside text-xs text-rose-700 space-y-0.5">
                                                            {item.missingRequired.map(f => (
                                                                <li key={f.key}>{safeText(f.label, f.key)}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {item.missingOptional.length > 0 && (
                                                    <div className="mt-2 ml-5">
                                                        <p className="text-xs font-bold text-amber-700 mb-1">Missing optional fields:</p>
                                                        <ul className="list-disc list-inside text-xs text-amber-700 space-y-0.5">
                                                            {item.missingOptional.map(f => (
                                                                <li key={f.key}>{safeText(f.label, f.key)}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-xs text-indigo-700 flex items-start">
                                            <i className="fas fa-lightbulb mr-2 mt-0.5"></i>
                                            <span>Click <strong>Edit Property</strong> on the property detail page to fill in missing {isTexas ? 'rental registration' : 'PRS registration'} fields. After updating, click Refresh on the {isTexas ? 'registration' : 'PRS'} Dashboard to re-check.</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 3: Download CSV Export */}
                        {step === 'EXPORT' && (
                            <div className="space-y-5 animate-in slide-in-from-right-4 duration-500">
                                <div className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100">
                                    <div className="flex items-center">
                                        <i className="fas fa-file-export text-2xl text-emerald-600 mr-4"></i>
                                        <div>
                                            <h3 className="font-bold text-emerald-900 text-lg">
                                                {isTexas ? 'Download Registration CSV Export' : 'Download PRS CSV Export'}
                                            </h3>
                                            <p className="text-xs text-emerald-700">
                                                {isTexas
                                                    ? 'Export your data as a municipal-portal-compatible CSV file'
                                                    : 'Export your data as a MHCLG-compatible CSV file'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                                    <p className="text-sm text-gray-600">
                                        {isTexas
                                            ? 'Download a CSV file containing all rental registration fields for your portfolio. You can upload this file to your city’s rental registration portal.'
                                            : 'Download a CSV file containing all PRS Database fields for your portfolio. You can upload this file to the MHCLG PRS Database portal.'}
                                    </p>

                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            onClick={() => handleExport()}
                                            disabled={exporting}
                                            className="flex flex-col items-center justify-center p-6 bg-indigo-50 border-2 border-indigo-200 rounded-xl hover:bg-indigo-100 hover:border-indigo-300 transition-all disabled:opacity-50"
                                        >
                                            <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-download'} text-2xl text-indigo-600 mb-2`}></i>
                                            <span className="font-bold text-indigo-800 text-sm">Portfolio CSV</span>
                                            <span className="text-[10px] text-indigo-600 mt-1">All properties</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                const firstProp = report?.items?.[0];
                                                if (firstProp) handleExport(firstProp.propertyId);
                                            }}
                                            disabled={exporting || !report?.items?.length}
                                            className="flex flex-col items-center justify-center p-6 bg-gray-50 border-2 border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all disabled:opacity-50"
                                        >
                                            <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-file-export'} text-2xl text-gray-600 mb-2`}></i>
                                            <span className="font-bold text-gray-800 text-sm">Sample Property CSV</span>
                                            <span className="text-[10px] text-gray-600 mt-1">First property only</span>
                                        </button>
                                    </div>

                                    {exportDownloaded && (
                                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center text-sm text-emerald-800">
                                            <i className="fas fa-check-circle text-emerald-500 mr-2"></i>
                                            CSV exported successfully. It will be attached to the Evidence Vault log.
                                        </div>
                                    )}
                                </div>

                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-600">
                                    <i className="fas fa-info-circle mr-1.5 text-indigo-500"></i>
                                    The CSV includes all {isTexas ? 'rental registration' : 'PRS Database'} required fields. Edit property fields directly in Landlord Pro and re-export as needed.
                                </div>
                            </div>
                        )}

                        {/* Step 4: Submission Checklist */}
                        {step === 'CHECKLIST' && (
                            <div className="space-y-5 animate-in slide-in-from-right-4 duration-500">
                                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200">
                                    <div className="flex items-center">
                                        <i className="fas fa-clipboard-check text-2xl text-slate-600 mr-4"></i>
                                        <div>
                                            <h3 className="font-bold text-slate-900 text-lg">Pre-Submission Checklist</h3>
                                            <p className="text-xs text-slate-600">
                                                {isTexas
                                                    ? 'Confirm each item before submitting to your municipal registration portal'
                                                    : 'Confirm each item before proceeding to the MHCLG portal'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                                    <div className="flex items-center p-4">
                                        <i className="fas fa-check-circle text-emerald-500 mr-3"></i>
                                        <div>
                                            <p className="font-medium text-gray-900 text-sm">Readiness score reviewed</p>
                                            <p className="text-xs text-gray-500">Portfolio readiness assessed at step 1</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center p-4">
                                        <i className={`fas fa-${report?.items?.filter(i => i.status !== 'GREEN').length === 0 ? 'check-circle text-emerald-500' : 'exclamation-circle text-amber-500'} mr-3`}></i>
                                        <div>
                                            <p className="font-medium text-gray-900 text-sm">Missing fields reviewed</p>
                                            <p className="text-xs text-gray-500">
                                                {report?.items?.filter(i => i.status !== 'GREEN').length === 0
                                                    ? `All properties have complete ${isTexas ? 'rental registration' : 'PRS'} data`
                                                    : `${report?.items?.filter(i => i.status !== 'GREEN').length || 0} properties still need attention`
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center p-4">
                                        <i className={`fas fa-${exportDownloaded ? 'check-circle text-emerald-500' : 'circle text-gray-300'} mr-3`}></i>
                                        <div>
                                            <p className="font-medium text-gray-900 text-sm">CSV exported</p>
                                            <p className="text-xs text-gray-500">
                                                {exportDownloaded ? 'CSV file downloaded' : 'Not yet exported — go back to step 3'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center p-4">
                                        <i className="fas fa-check-circle text-emerald-500 mr-3"></i>
                                        <div>
                                            <p className="font-medium text-gray-900 text-sm">{isTexas ? 'Registration fields populated' : 'PRS fields populated'}</p>
                                            <p className="text-xs text-gray-500">Registration numbers and dates entered in property details</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center p-4">
                                        <i className="fas fa-circle text-gray-300 mr-3"></i>
                                        <div>
                                            <p className="font-medium text-gray-900 text-sm">
                                                {isTexas ? 'Understand your municipal submission process' : 'Understand MHCLG submission process'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {isTexas
                                                    ? 'Submission is done via your city’s rental registration portal — use the CSV export or the in-app Texas Registrations hub'
                                                    : 'Submission is done via the official MHCLG PRS Database portal — CSV upload available once portal opens'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {!exportDownloaded && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-start">
                                        <i className="fas fa-exclamation-triangle mr-2 mt-0.5"></i>
                                        <span>CSV not yet exported. Go back to Step 3 to download your {isTexas ? 'registration' : 'PRS'} data package.</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 5: Submit / MHCLG Portal Link */}
                        {step === 'SUBMIT' && (
                            <div className="space-y-5 animate-in zoom-in-95 duration-500">
                                <div className="p-6 bg-indigo-50 rounded-2xl border border-indigo-100 text-center">
                                    <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                                        <i className="fas fa-rocket text-2xl text-indigo-600"></i>
                                    </div>
                                    <h3 className="font-bold text-indigo-900 text-lg">
                                        {isTexas ? 'Ready for Municipal Registration' : 'Ready for PRS Database Submission'}
                                    </h3>
                                    <p className="text-sm text-indigo-700 mt-1 max-w-md mx-auto">
                                        {isTexas
                                            ? 'Your data is prepared. Open the Texas Registrations hub to manage municipal registration for each property.'
                                            : 'Your data is prepared. Open the MHCLG PRS Database portal to upload your CSV and complete registration.'}
                                    </p>
                                </div>

                                <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                                    <a
                                        href={isTexas ? '/texas/registrations' : 'https://www.gov.uk/government/collections/private-rented-sector-database'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-3 w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg"
                                    >
                                        <i className="fas fa-external-link-alt"></i>
                                        {isTexas ? 'Open Texas Registrations Hub' : 'Open MHCLG PRS Database Portal'}
                                    </a>
                                    <p className="text-xs text-gray-500 text-center">
                                        {isTexas
                                            ? 'This opens the in-app Texas Registrations hub where municipal registrations are managed per property.'
                                            : 'This link opens the official UK Government PRS Database information page in a new tab.'}
                                    </p>
                                </div>

                                {completed ? (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                                        <i className="fas fa-check-circle text-2xl text-emerald-500 mb-1"></i>
                                        <p className="font-bold text-emerald-800">Completion logged to Evidence Vault</p>
                                        <p className="text-xs text-emerald-600 mt-1">Closing automatically...</p>
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                        <h4 className="font-bold text-gray-800 text-sm mb-3">Summary</h4>
                                        <div className="grid grid-cols-2 gap-3 text-xs">
                                            <div className="flex items-center gap-2">
                                                <i className="fas fa-check-circle text-emerald-500"></i>
                                                <span>Readiness checked</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <i className={`fas fa-${report?.items?.filter(i => i.status !== 'GREEN').length === 0 ? 'check-circle text-emerald-500' : 'exclamation-circle text-amber-500'}`}></i>
                                                <span>Fields reviewed</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <i className={`fas fa-${exportDownloaded ? 'check-circle text-emerald-500' : 'times-circle text-gray-400'}`}></i>
                                                    <span>CSV {exportDownloaded ? 'exported' : 'not exported'}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <i className="fas fa-check-circle text-emerald-500"></i>
                                                <span>{isTexas ? 'Registrations hub provided' : 'MHCLG link provided'}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Navigation Footer */}
                    <div className="flex justify-between mt-6 pt-4 border-t">
                        <button
                            onClick={step === 'READINESS' ? onClose : handleBack}
                            className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                        >
                            {step === 'READINESS' ? 'Cancel' : 'Back'}
                        </button>

                        <div className="flex gap-3">
                            {step === 'SUBMIT' ? (
                                <button
                                    onClick={logCompletion}
                                    disabled={logging || completed}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-bold"
                                >
                                    {logging ? <><i className="fas fa-spinner fa-spin mr-2"></i>Logging...</> : completed ? 'Completed' : 'Complete & Log to Evidence Vault'}
                                </button>
                            ) : (
                                <button
                                    onClick={handleNext}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-bold"
                                >
                                    Next
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
